import React, { useState, Suspense, useCallback } from 'react';
import IconModt from './components/iconModt';

import { useInspectShow } from './hooks/useInspectShow';
import { useCopyFeedback } from './hooks/useCopyFeedback';
import { usePopupViewEffect } from './hooks/usePopupViewEffect';
import { useTriggerLogic } from './hooks/useTriggerLogic';
import { useBannerState } from './hooks/useBannerState';
import { useBodyScrollLock } from './hooks/useBodyScrollLock';
import { useIconShowTracking } from './hooks/useIconShowTracking';
import { checkDiscountLogic } from './services/discountLogic';
import { canShowIconBeforeWithFrequency } from '@/service/strategy/iconTiming';

// @ts-ignore
// biome-ignore format: 构建问题，不能格式化
import(/* webpackChunkName: "cartAnimateCss" */ './styles/App.css');

/** 弹窗主组件（懒加载）- 普通弹窗 */
// biome-ignore format: 构建问题，不能格式化
const Tanchuang = React.lazy(() => import(/* webpackChunkName: "Tanchuang" */ './tools/tanChuang'));
/** 弹窗主组件（懒加载）- 支持自定义步骤的新版弹窗 */
// biome-ignore format: 构建问题，不能格式化
const TanchuangNew = React.lazy(() => import(/* webpackChunkName: "TanchuangNew" */ './tools/tanChuang/indexNew'));
/** 转盘组件（懒加载） */
// biome-ignore format: 构建问题，不能格式化
const Zhuanpan = React.lazy(() => import(/* webpackChunkName: "Zhuanpan" */ './tools/zhuanPan'));
/** 底部 Banner 组件（懒加载） */
// biome-ignore format: 构建问题，不能格式化
const Banner = React.lazy(() => import(/* webpackChunkName: "banner" */ './tools/tanChuang/banner'));
/** 浮条 Banner 组件（懒加载） */
// biome-ignore format: 构建问题，不能格式化
const FloatBanner = React.lazy(() => import(/* webpackChunkName: "floatBanner" */ './tools/tanChuang/floatBanner'));

/**
 * 弹窗根组件：组合各类触发/展示/埋点 hooks，按 type 渲染对应弹窗或转盘
 */
function App(props: any) {
  const {
    settings: oldSettings, // 初始弹窗配置（可从接口或父级传入）
    type, // 弹窗类型：'POP_WINDOW' | 'WheelOfFortune' 等
    expand, // 转盘是否展开
    id, // 表单/弹窗唯一 id
    combinationType, // 组合弹窗类型（如第二步立即弹出）
    triggerEventType, // 触发方式：0 延迟 1 离开 2 加购 等
    responseData, // 接口下发的完整配置与数据
    externalRes, // 外部资源（如加购图片、平台名）
    upgradeLoad, // 升级加载回调
    extraTime = 0, // 已过去时间（用于计算剩余延迟）
    actionType, // 动作类型（如弃单 1/5）
    triggerSource, // 触发来源（如 leaveBeta）
    name, // 表单/弹窗名称
    loadStartTime, // 加载开始时间（性能上报）
    coverShow, // 是否展示封面
  } = props;

  /** 当前弹窗配置（可被折扣码接口回写） */
  const [settings, setSettings] = useState(oldSettings);
  /** 弹窗是否展示（经优先级/策略校验） */
  const [show, setShow] = useInspectShow({
    id,
    name,
    combinationType,
    triggerEventType,
  });
  /** 是否处于「已复制」提示展示中 */
  const [copiedShow, setCopiedShow] = useState(false);
  /** 倒计时剩余秒数（用于弹窗内倒计时展示） */
  const [countDownTime, setCountDownTime] = useState(
    settings?.rules?.countDown ?? 0,
  );
  /** 转盘/活动获得的优惠券码 */
  const [couponCode, setCouponCode] = useState('');
  /** 是否展示悬浮 icon（弹窗被拦截时展示） */
  const [iconShow, setIconShow] = useState(false);
  /** 弹窗步骤：'cover' | 'end' | '' 等，用于封面/结束态与 Banner 联动 */
  const [tanChuangStep, setTanChuangStep] = useState('');

  /** 若需先拉折扣再展示则执行 checkDiscountLogic 后 callback，供触发逻辑调用 */
  const openWithDiscountCheck = useCallback(
    (callback: () => void) => {
      checkDiscountLogic({
        settings,
        responseData,
        id,
        setSettings,
        callback,
      });
    },
    [settings, responseData, id],
  );

  useCopyFeedback({
    copiedShow,
    type,
    settings,
    couponCode,
    setCopiedShow,
    triggerEventType,
    id,
    name,
  });

  usePopupViewEffect({
    show,
    tanChuangStep,
    iconShow,
    type,
    id,
    name,
    triggerEventType,
    responseData,
    settings,
    externalRes,
    triggerSource,
  });

  useTriggerLogic({
    setShow,
    setIconShow,
    settings,
    responseData,
    id,
    triggerEventType,
    combinationType,
    expand,
    extraTime,
    actionType,
    triggerSource,
    openWithDiscountCheck,
  });

  /** 弹窗展示时锁定 body 滚动（全屏模式始终锁定） */
  useBodyScrollLock({ show, responseData, settings });
  /** icon 展示时上报 float_ad_view */
  useIconShowTracking({ iconShow, triggerEventType, id, name });

  /** Banner/浮条展示状态及配置（由步骤与 settings 派生） */
  const {
    bannerShow, // 是否展示底部 Banner
    floatBannerShow, // 是否展示浮条
    bannerInfo, // 传给 Banner 组件的配置（含优惠券等）
  } = useBannerState({
    settings,
    type,
    tanChuangStep,
    show,
    couponCode,
    triggerEventType,
    id,
    name,
  });

  /** 弹窗内部回调：上报当前步骤，用于封面/结束与 Banner 联动 */
  const getStep = useCallback((step: string) => {
    setTanChuangStep(step);
  }, []);

  return (
    <>
      <div
        className="cartx-a-message-copy cartx-b-message-copy"
        style={{ display: copiedShow ? '' : 'none' }}
      >
        copied
      </div>
      {iconShow && !show && (
        <IconModt
          position={settings?.position}
          show={show}
          setShow={setShow}
          triggerEventType={triggerEventType}
          id={id}
          name={name}
          scheduleType={responseData?.settings?.targeting?.scheduleType}
          startTime={responseData?.settings?.targeting?.startTime}
          endTime={responseData?.settings?.targeting?.endTime}
          onExpire={() => setIconShow(false)}
        />
      )}
      {type === 'POP_WINDOW' &&
        settings?.rules?.endCloseColor !== undefined && (
          <TanchuangNew
            show={show}
            setShow={setShow}
            settings={settings}
            texts={settings?.texts}
            combinationType={combinationType}
            rules={settings?.rules}
            countDownTime={countDownTime}
            setCountDownTime={setCountDownTime}
            setCopiedShow={setCopiedShow}
            startList={settings?.start}
            endList={settings?.end}
            id={id}
            setIconShow={setIconShow}
            externalRes={externalRes}
            triggerEventType={triggerEventType}
            callback={getStep}
            upgradeLoad={upgradeLoad}
            responseData={responseData}
            loadStartTime={loadStartTime}
            name={name}
            coverShow={coverShow}
          />
        )}
      {type === 'POP_WINDOW' &&
        settings?.rules?.endCloseColor === undefined && (
          <Tanchuang
            show={show}
            setShow={setShow}
            settings={settings}
            texts={settings?.texts}
            combinationType={combinationType}
            rules={settings?.rules}
            countDownTime={countDownTime}
            setCountDownTime={setCountDownTime}
            setCopiedShow={setCopiedShow}
            startList={settings?.start}
            endList={settings?.end}
            id={id}
            setIconShow={setIconShow}
            externalRes={externalRes}
            triggerEventType={triggerEventType}
            callback={getStep}
            upgradeLoad={upgradeLoad}
            responseData={responseData}
            loadStartTime={loadStartTime}
            name={name}
            coverShow={coverShow}
          />
        )}
      {type === 'WheelOfFortune' && (
        <Suspense fallback={<div>loading...</div>}>
          <Zhuanpan
            show={show}
            setShow={setShow}
            texts={settings?.texts}
            gdpr={settings?.gdpr}
            combinationType={combinationType}
            wheelSlices={settings?.wheelSlices}
            id={id}
            setCopiedShow={setCopiedShow}
            rules={settings?.rules}
            couponCode={couponCode}
            setCouponCode={setCouponCode}
            setIconShow={setIconShow}
            startList={settings?.start}
            settings={settings}
            triggerEventType={triggerEventType}
            upgradeLoad={upgradeLoad}
            responseData={responseData}
            name={name}
          />
        </Suspense>
      )}
      {bannerShow && (
        <Suspense fallback={<div>loading...</div>}>
          <Banner
            bannerInfo={bannerInfo}
            triggerEventType={triggerEventType}
            id={id}
            name={name}
          />
        </Suspense>
      )}
      {floatBannerShow && (
        <Suspense fallback={<div>loading...</div>}>
          <FloatBanner
            bannerInfo={bannerInfo}
            setCopiedShow={setCopiedShow}
            triggerEventType={triggerEventType}
            id={id}
            name={name}
          />
        </Suspense>
      )}
    </>
  );
}

export default App;
